import { useState } from "react";
import { notNull } from "shelving";
import { useMount } from "shelving/react";
import { getMostVisibleObserverEntry } from "../util/scroll.js";
import { Icon } from "./Icon.js";

/** Component that lets you slide through images by either clicking on the left/right sides or swiping left/right. */
export function Slides({ mods, children }: { mods?: string | undefined; children: (React.ReactNode | React.ReactNode[])[] }): React.ReactElement | null {
	const flat: React.ReactNode[] = children.flat().filter(notNull);
	return flat.length > 1 ? <MultiSlide mods={mods}>{flat}</MultiSlide> : flat.length > 0 ? <MonoSlide mods={mods}>{flat}</MonoSlide> : null;
}

function MonoSlide({ mods = "", children }: { mods?: string | undefined; children: React.ReactNode }): React.ReactElement {
	return (
		<figure className={mods} aria-roledescription="slides">
			<div className="slides">{children}</div>
		</figure>
	);
}

function MultiSlide({ mods = "", children }: { mods?: string | undefined; children: React.ReactNode[] }): React.ReactElement {
	const [active, setActive] = useState<number>(0);
	return (
		<figure className={mods}>
			<div
				className="slides"
				aria-roledescription="slides"
				ref={useMount<HTMLDivElement, [React.ReactNode]>(slides => {
					const observer = new IntersectionObserver(
						entries => {
							const entry = getMostVisibleObserverEntry(entries);
							if (entry) setActive(entries.indexOf(entry));
						},
						{ root: slides, threshold: Array(slides.childElementCount).fill(0.51) },
					);
					for (const slide of slides.children) observer.observe(slide);
					return () => observer.disconnect();
				}, children)}
			>
				{children}
			</div>
			<div className="dots pin-bottom">
				{children.map((child, i) => (
					<span key={i} className={`dot ${active === i ? "dot-active" : ""}`} />
				))}
			</div>
			<button //
				type="button"
				className="button pin-bottom pin-left"
				onClick={({ currentTarget }) =>
					setActive(x => {
						const next = Math.max(x - 1, 0);
						currentTarget.parentElement?.firstElementChild?.children[next]?.scrollIntoView(); // Scroll the next slide into view.
						return next;
					})
				}
				title="Previous"
				disabled={active <= 0}
			>
				<Icon icon="left" />
			</button>
			<button //
				type="button"
				className="button pin-bottom pin-right"
				onClick={({ currentTarget }) =>
					setActive(x => {
						const next = Math.min(x - 1, children.length - 1);
						currentTarget.parentElement?.firstElementChild?.children[next]?.scrollIntoView(); // Scroll the next slide into view.
						return next;
					})
				}
				title="Next"
				disabled={active >= children.length - 1}
			>
				<Icon icon="right" />
			</button>
		</figure>
	);
}
